import { Box, CircularProgress, Stack } from '@mui/material';
import { BaseHit, Hit } from 'instantsearch.js';
import { dissoc, filter, groupBy, head, isNotNil, map, pipe, values, when } from 'ramda';
import { useHits, UseHitsProps, useInstantSearch } from 'react-instantsearch';

import { isLoadingOrStalled } from '../../util/isLoadingOrStalled';
import { SearchPagination } from '../SearchPagination/SearchPagination';
import { SearchNoResult } from '~/components/SearchNoResult/SearchNoResult';
import { useSearchDomainConfig } from '~/routes/Search/components/SearchDomainConfigProvider';
import { SearchDomain } from '~/routes/Search/types/SearchDomain.ts';

/**
 * Groups an array of search result hits by the first value in their `group_key` property,
 * removes any items without a group, and returns an array containing only the first item from each group.
 * For example, sourcing project lot items are grouped by their sourcing project,
 * and only the first item of each group is returned.
 */
const groupItems = <T extends BaseHit>(items: Hit<T>[]): Hit<T>[] =>
  pipe(
    groupBy<Hit<T>, string>((item: Hit<T>) => item?.group_key[0] ?? 'ungrouped'),
    dissoc('ungrouped'),
    values,
    filter<Hit<T>[] | undefined, Hit<T>[]>(isNotNil),
    map<Hit<T>[], Hit<T>>(head),
  )(items);

export interface SearchResultHitsProps extends UseHitsProps {
  domain: SearchDomain;
}

export const SearchResultHits = ({ domain, ...props }: SearchResultHitsProps) => {
  const { searchConfigByDomain } = useSearchDomainConfig();
  const { items: rawItems } = useHits(props);
  const searchConfig = searchConfigByDomain(domain);
  const { status } = useInstantSearch();

  const items = pipe(when(() => Boolean(searchConfig.groupBy), groupItems))(rawItems);
  const hasItems = items.length > 0;
  const loadingOrStalled = isLoadingOrStalled(status);

  return (
    <>
      <Stack direction="column" spacing={2}>
        {loadingOrStalled ? (
          <Box
            sx={{
              display: 'flex',
              flex: '1 1 0',
              justifyContent: 'center',
            }}
          >
            <CircularProgress size={30} />
          </Box>
        ) : hasItems ? (
          items.map((hit: Hit<BaseHit>) => {
            const SearchHitComponent = searchConfig.hitComponent;
            return <SearchHitComponent key={hit.id} hit={hit} />;
          })
        ) : (
          <SearchNoResult />
        )}
      </Stack>
      {/*
      the pagination must always be there (no conditional or exchange with virtual)
      otherwise it could end up in an endless render loop
      */}
      <SearchPagination hidden={!hasItems || loadingOrStalled} />
    </>
  );
};
